import { useEffect, useRef } from 'react';

const areas = [
  {
    icon: 'ri-scales-line',
    title: 'Inventário',
    tag: 'Principal',
    description:
      'Conduzimos todo o processo de inventário judicial ou extrajudicial com agilidade e segurança, garantindo que a partilha de bens seja realizada da forma mais eficiente possível.',
    highlight: true,
  },
  {
    icon: 'ri-home-gear-line',
    title: 'Usucapião',
    tag: '',
    description:
      'Regularizamos imóveis por usucapião, transformando a posse prolongada em propriedade legal com os melhores fundamentos jurídicos.',
    highlight: false,
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Regularização de Imóveis',
    tag: '',
    description:
      'Resolvemos pendências documentais, averbações, retificações e todas as irregularidades que impedem a plena utilização ou venda de um imóvel.',
    highlight: false,
  },
  {
    icon: 'ri-building-line',
    title: 'Direito Imobiliário',
    tag: '',
    description:
      'Assessoria completa em compra, venda, locação, contratos e disputas imobiliárias. Protegemos seu investimento em cada transação.',
    highlight: false,
  },
];

function useScrollRevealList() {
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    refs.current.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(28px)';
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              if (!el) return;
              el.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
              el.style.opacity = '1';
              el.style.transform = 'translateY(0)';
            }, i * 120);
            obs.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      obs.observe(el);
    });
  }, []);

  return refs;
}

export default function PracticeAreas() {
  const listRefs = useScrollRevealList();

  return (
    <section id="atuacao" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-px bg-[#C7A76C]"></div>
            <span className="font-sans text-[#C7A76C] text-xs font-medium tracking-widest uppercase">
              Áreas de Atuação
            </span>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#0F4A2B] font-semibold leading-tight">
            Soluções jurídicas para{' '}
            <span className="text-[#C7A76C] italic">proteger seu patrimônio</span>
          </h2>
          <p className="font-sans text-[#3A3A3A] text-base mt-5 leading-relaxed">
            Cada área de atuação foi cuidadosamente escolhida para oferecer a solução mais completa
            em questões que envolvem patrimônio, herança e regularização de imóveis.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={area.title}
              ref={(el) => { listRefs.current[i] = el; }}
              className={`group relative flex flex-col gap-5 rounded-2xl p-7 border transition-all duration-400 cursor-default ${
                area.highlight
                  ? 'bg-[#0F4A2B] border-[#0F4A2B]'
                  : 'bg-white border-[#E8E4DC] hover:border-[#C7A76C]/40 hover:bg-[#F5F3EE]'
              }`}
            >
              {/* Tag */}
              {area.tag && (
                <span className="absolute top-5 right-5 text-[10px] font-sans font-semibold bg-[#C7A76C]/15 text-[#C7A76C] px-2.5 py-1 rounded-full tracking-wide uppercase">
                  {area.tag}
                </span>
              )}

              {/* Icon */}
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-xl ${
                  area.highlight ? 'bg-[#C7A76C]/20' : 'bg-[#0F4A2B]/8 group-hover:bg-[#C7A76C]/10'
                } transition-colors duration-300`}
              >
                <i
                  className={`${area.icon} text-2xl ${
                    area.highlight ? 'text-[#C7A76C]' : 'text-[#0F4A2B]'
                  }`}
                ></i>
              </div>

              {/* Content */}
              <div className="flex flex-col gap-3 flex-1">
                <h3
                  className={`font-serif text-xl font-semibold ${
                    area.highlight ? 'text-white' : 'text-[#0F4A2B]'
                  }`}
                >
                  {area.title}
                </h3>
                <p
                  className={`font-sans text-sm leading-relaxed ${
                    area.highlight ? 'text-white/70' : 'text-[#3A3A3A]'
                  }`}
                >
                  {area.description}
                </p>
              </div>

              {/* CTA link */}
              <a
                href="https://wa.me/5511958814422"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className={`flex items-center gap-1.5 text-xs font-sans font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap mt-auto ${
                  area.highlight
                    ? 'text-[#C7A76C] hover:text-[#D6BA85]'
                    : 'text-[#0F4A2B] hover:text-[#C7A76C]'
                }`}
              >
                Saiba mais
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-16 bg-[#F5F3EE] border border-[#C7A76C]/20 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-serif text-2xl text-[#0F4A2B] font-semibold">
              Não sabe por onde começar?
            </p>
            <p className="font-sans text-[#3A3A3A] text-sm mt-1.5">
              Entre em contato agora e descubra a melhor solução para o seu caso sem compromisso.
            </p>
          </div>
          <a
            href="https://wa.me/5511958814422?text=Olá%2C%20preciso%20de%20ajuda%20jurídica"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 bg-[#C7A76C] hover:bg-[#D6BA85] text-[#0C3A22] font-sans font-semibold text-sm px-8 py-3.5 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-base"></i>
            Agende sua Consulta
          </a>
        </div>
      </div>
    </section>
  );
}
